/**
 * A count of events and the students behind it (Sep 11 2026).
 *
 * Built on the suppression core, never changing it (`suppression.ts` is
 * contractual). It applies two rules the dashboard already keeps:
 *
 *  - The TOTAL publishes only when `floor` students or more are behind it.
 *    Twelve questions asked by four students is a figure about four people.
 *    A real zero publishes as zero.
 *  - The STUDENT COUNT publishes only when its share of the denominator
 *    does, which carries the core's complement rule: "30 of 36 played a
 *    mentor video" would publish the six who did not.
 *
 * The Worker builds its snapshot with this and the sample builds the demo
 * with it, so the two cannot drift apart.
 */

import { K, count, rate, suppressed, type Cell, type Rate } from "~/lib/suppression";

export interface EventFigureCells {
  readonly total: Cell<number>;
  readonly students: Cell<number>;
  readonly share: Cell<Rate>;
}

export function eventFigure(
  total: number,
  students: number,
  denominator: number,
  floor: number = K,
): EventFigureCells {
  const share = rate(students, denominator);
  return {
    total:
      students === 0
        ? count(0)
        : students >= floor
          ? count(total)
          : suppressed("below-threshold"),
    students: share.kind === "value" ? count(students) : share,
    share,
  };
}
